import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { RESTAURANT_BY_ID } from "../constants";

const PACIFIC_DATE_TIME = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  dateStyle: "medium",
  timeStyle: "short",
});

function activeLineStatus(line, request) {
  return line.status || (line.purchased ? "completed" : request.status);
}

function buildGroups(requests, products, suppliers) {
  const productById = new Map(products.map((product) => [product.id, product]));
  const supplierById = new Map(suppliers.map((supplier) => [supplier.id, supplier]));
  const groups = new Map();

  (requests || [])
    .filter((request) => request.type === "purchase")
    .forEach((request) => {
      const restaurant = RESTAURANT_BY_ID[request.restaurant]?.name || "Unknown restaurant";
      const supplier = supplierById.get(request.vendor)?.name || "Unknown supplier";
      const groupKey = `${restaurant}::${supplier}`;
      if (!groups.has(groupKey)) groups.set(groupKey, { restaurant, supplier, rows: [] });

      (request.items || []).forEach((line) => {
        const status = activeLineStatus(line, request);
        if (status === "completed" || status === "fulfilled" || status === "rejected" || status === "supplier_unavailable") return;
        const product = productById.get(line.productId);
        const quantity = Number(line.quantity ?? line.qty ?? 0);
        const unit = line.unit || product?.unit || "";
        groups.get(groupKey).rows.push([
          request.requestedByName || "—",
          product?.name || line.productName || "Unknown product",
          `${quantity}${unit ? ` ${unit}` : ""}`,
        ]);
      });
    });

  return [...groups.values()]
    .filter((group) => group.rows.length > 0)
    .sort((a, b) =>
      a.restaurant.localeCompare(b.restaurant) ||
      a.supplier.localeCompare(b.supplier)
    );
}

export function buildPdfFilename() {
  return `cafe-breizh-purchase-requests-${new Date().toISOString().slice(0, 10)}.pdf`;
}

async function loadBrandMark() {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      canvas.getContext("2d").drawImage(image, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    image.onerror = () => resolve(null);
    image.src = "/cafe-breizh-print-logo.png";
  });
}

export async function downloadRequestsPdf(filename, requests, products, suppliers) {
  const groups = buildGroups(requests, products, suppliers);
  if (!groups.length) return false;

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 42;

  const logo = await loadBrandMark();
  if (logo) doc.addImage(logo, "PNG", margin, 28, 42, 42);

  const titleX = logo ? margin + 54 : margin;
  doc.setTextColor(11, 31, 58);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("Cafe Breizh - Purchase Requests", titleX, 43);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(65, 72, 82);
  doc.text(`Date: ${PACIFIC_DATE_TIME.format(new Date())}`, titleX, 60);
  doc.setDrawColor(49, 95, 155);
  doc.setLineWidth(1);
  doc.line(margin, 78, pageWidth - margin, 78);

  let cursorY = 94;
  groups.forEach((group) => {
    const estimatedHeight = 45 + group.rows.length * 22;
    if (cursorY + Math.min(estimatedHeight, 120) > doc.internal.pageSize.getHeight() - 42) {
      doc.addPage();
      cursorY = 42;
    }

    doc.setFillColor(250, 250, 248);
    doc.setDrawColor(49, 95, 155);
    doc.setLineWidth(0.8);
    doc.roundedRect(margin, cursorY, pageWidth - margin * 2, 28, 4, 4, "FD");
    doc.setTextColor(11, 31, 58);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(`${group.restaurant}  |  ${group.supplier}`, margin + 10, cursorY + 18);

    autoTable(doc, {
      startY: cursorY + 34,
      margin: { left: margin, right: margin },
      head: [["Requested by", "Product", "Quantity"]],
      body: group.rows,
      theme: "grid",
      styles: {
        font: "helvetica",
        fontSize: 9,
        cellPadding: 6,
        lineColor: [220, 216, 207],
        lineWidth: 0.5,
        textColor: [11, 31, 58],
      },
      headStyles: {
        fillColor: [250, 250, 248],
        textColor: [11, 31, 58],
        lineColor: [49, 95, 155],
        lineWidth: 0.6,
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 120 },
        2: { cellWidth: 82, halign: "right" },
      },
      didDrawPage: () => {
        const pageNumber = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(120, 126, 136);
        doc.text(`Page ${pageNumber}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 20, { align: "right" });
      },
    });

    cursorY = (doc.lastAutoTable?.finalY || cursorY + 34) + 18;
  });

  doc.save(filename);
  return true;
}
